// Dana Voice client (push-to-talk pilot). Presentation only — the backend
// re-checks the dana_voice entitlement on every call, fail-closed.
import { withAuth } from './hrApi';

// Same cookie-session auth + silent-refresh behavior as every other API
// client; longer timeout because a turn = upload + STT + Dana.
const client = withAuth(import.meta.env.VITE_AI_URL || 'http://localhost:8001');
client.defaults.timeout = 45000;

export interface VoiceProposal {
  action_id: string;
  tool: string;
  summary: string;
  payload: Record<string, unknown>;
  payload_sha256: string;
  status: string;
  expires_at: string;
}

export interface VoiceTurnResult {
  transcript: { text: string; language?: string; needs_review?: boolean };
  reply: string;
  reply_audio_b64?: string | null; // natural-voice mp3 (server TTS)
  reply_audio_mime?: string | null;
  tools_used: string[];
  proposals: VoiceProposal[];
}

export const voiceAvailability = () =>
  client.get('/api/v1/ai/voice/availability').then((r) => r.data as {
    enabled: boolean; entitled: boolean; stt_configured: boolean; localization: boolean;
  });

export const voiceTurn = (blob: Blob, mime: string): Promise<VoiceTurnResult> => {
  const form = new FormData();
  form.append('audio', blob, mime.includes('webm') ? 'clip.webm' : 'clip.m4a');
  let tz: string | undefined;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* zone-less */ }
  if (tz) form.append('timezone', tz);
  const locale = localStorage.getItem('preferred-locale');
  if (locale) form.append('locale', locale);
  form.append('surface', 'web');
  form.append('client_turn_id', crypto.randomUUID());
  return client.post('/api/v1/ai/voice/turn', form).then((r) => r.data);
};

export const confirmVoiceAction = (actionId: string, proposalSha256?: string) =>
  client
    .post(`/api/v1/ai/actions/${actionId}/confirm`, { proposal_sha256: proposalSha256 })
    .then((r) => r.data as { status: string; result?: unknown });

export const cancelVoiceAction = (actionId: string) =>
  client.post(`/api/v1/ai/actions/${actionId}/cancel`).then((r) => r.data);
